// src/components/UI/RoleSelect.jsx
//
// The one role picker used across the app. Roles are dynamic — each is a
// role-scoped PermissionSet on the server — so this select is always in step with
// Permission Sets, the Dashboard Builder (one dashboard per role) and the
// approval templates (approvers resolved by role key).
//
// With `allowCreate`, a new role can be minted from inside the form: the footer
// opens a small dialog, the role is created, the list refreshes and the new role
// is selected. Creating needs the same grant as authoring a permission set
// (permission:create), so the footer hides for everyone else.
import React, { useState } from 'react';
import { Select, Divider, Button, Modal, Form, Input, InputNumber, Space, Typography, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useRoles } from '../../hooks/useRoles';
import { createRole } from '../../services/rolesApi';
import { usePermissions } from '../../hooks/usePermissions';

const { Text } = Typography;

// "Regional Head" → "REGIONAL_HEAD" — mirrors the server's derivation so the
// preview shown while typing matches what actually gets stored.
export const keyFromName = (name) => String(name || '')
  .trim()
  .toUpperCase()
  .replace(/[^A-Z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 40);

export default function RoleSelect({
  value,
  onChange,
  allowCreate = false,
  placeholder = 'Select role',
  disabled = false,
  style,
  ...rest
}) {
  const { roles, options, loading, reload } = useRoles(value);
  const { can } = usePermissions();
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const nameWatch = Form.useWatch('name', form);
  const keyWatch = Form.useWatch('key', form);

  const canCreate = allowCreate && can('permission', 'create');
  const previewKey = keyFromName(keyWatch || nameWatch);

  const openAdd = () => { form.resetFields(); setAddOpen(true); };

  const submitAdd = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const created = await createRole({
        name: v.name.trim(),
        key: keyFromName(v.key || v.name),
        description: v.description,
        priority: v.priority || 0,
        copyFromKey: v.copyFromKey || undefined,
      });
      message.success(`Role "${created.name}" created`);
      setAddOpen(false);
      await reload();
      onChange?.(created.key);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to create role');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Select
        showSearch
        optionFilterProp="label"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={options}
        loading={loading}
        disabled={disabled}
        style={{ width: '100%', ...style }}
        popupRender={canCreate ? (menu) => (
          <>
            {menu}
            <Divider style={{ margin: '6px 0' }} />
            <Button type="link" size="small" icon={<PlusOutlined />} onClick={openAdd} style={{ paddingInline: 8 }}>
              Add new role
            </Button>
          </>
        ) : undefined}
        {...rest}
      />

      <Modal
        title="Add Role"
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={submitAdd}
        okText="Create role"
        okButtonProps={{ loading: saving }}
        width={480}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          A role is a permission set. Creating it here also gives it its own dashboard in
          the Dashboard Builder and makes it selectable as an approver role.
        </Text>
        <Form form={form} layout="vertical" style={{ marginTop: 16 }} initialValues={{ priority: 0 }}>
          <Form.Item name="name" label="Role name" rules={[{ required: true, message: 'Enter a role name' }]}>
            <Input placeholder="e.g. Regional Head" />
          </Form.Item>
          <Form.Item
            name="key"
            label="Key"
            tooltip="Stable machine identifier stored on the employee. Derived from the name if left blank."
            extra={previewKey ? <Text type="secondary" style={{ fontSize: 12 }}>Stored as <Text code>{previewKey}</Text></Text> : null}
            rules={[{
              validator: (_, val) => {
                const k = keyFromName(val || nameWatch);
                if (!k) return Promise.resolve();
                return /^[A-Z][A-Z0-9_]{1,39}$/.test(k)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Must start with a letter; letters, numbers and underscores only (2–40 chars)'));
              },
            }]}
          >
            <Input placeholder="Auto-generated from the name" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="What this role is for (optional)" />
          </Form.Item>
          <Space align="start" size={12} style={{ display: 'flex' }}>
            <Form.Item
              name="copyFromKey"
              label="Copy permissions from"
              tooltip="Start from an existing role's access instead of none. Editable afterwards in Permission Sets."
              style={{ flex: 1, minWidth: 220 }}
            >
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Start with no permissions"
                options={roles.map((r) => ({ value: r.key, label: r.name }))}
              />
            </Form.Item>
            <Form.Item
              name="priority"
              label="Priority"
              tooltip="Higher priority wins when a user belongs to several sets."
              style={{ width: 120 }}
            >
              <InputNumber min={0} precision={0} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Tag color="blue" style={{ whiteSpace: 'normal' }}>
            Fine-tune this role's access afterwards in Permission Sets.
          </Tag>
        </Form>
      </Modal>
    </>
  );
}
